import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Share, Smartphone, X } from 'lucide-react';
import { Button } from '@/design/components';
import { useSettings } from '@/access/SettingsContext';
import { detectPlatform, isStandalone } from './platform';
import { useInstallPrompt } from './useInstallPrompt';

/**
 * הזמנה להתקין, כל עוד האפליקציה אינה מותקנת.
 *
 * באנדרואיד הכפתור באמת מתקין: Chrome נותן אירוע beforeinstallprompt,
 * ולחיצה פותחת את חלון ההתקנה שלו.
 *
 * באייפון אין דבר כזה. אפל לא חושפת שום API שמוסיף אתר למסך הבית,
 * וההוספה חייבת לעבור דרך תפריט השיתוף של ספארי. לכן כאן הכפתור
 * פותח את שלושת הצעדים ולא מבטיח התקנה — כפתור שכתוב עליו "התקנה"
 * ולא מתקין גרוע מהסבר.
 *
 * נסגר לשבועיים ולא לתמיד: מי שלא רוצה עכשיו לא אמור לראות את זה
 * בכל כניסה, ומי ששכח כן אמור להיזכר. ההתקנה נשארת תמיד זמינה
 * באזור האישי.
 */
const DISMISS_KEY = 'install-card-dismissed';
const SNOOZE_DAYS = 14;

function dismissedRecently(): boolean {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < SNOOZE_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    // מצב פרטי, או אחסון חסום. עדיף להציג מאשר להיעלם בשקט
    return false;
  }
}

export function InstallCard() {
  const { brand } = useSettings();
  const platform = detectPlatform();
  const { canPrompt, promptInstall } = useInstallPrompt();

  const [isHidden, setIsHidden] = useState(() => isStandalone() || dismissedRecently());
  const [showSteps, setShowSteps] = useState(false);

  if (isHidden) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* לא נורא. ההסתרה תחזיק עד הרענון הבא */
    }
    setIsHidden(true);
  };

  return (
    <section className="grid gap-3 rounded-lg bg-raised p-4 shadow-card">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-sm bg-olive-soft">
          <Smartphone size={20} strokeWidth={1.75} aria-hidden="true" className="text-ink" />
        </span>
        <div className="grid grow gap-0.5">
          <h2 className="m-0 font-display text-title font-semibold text-ink">
            להוסיף את {brand.app_title} למסך הבית
          </h2>
          <p className="m-0 text-body-sm text-muted">
            נפתחת כמו אפליקציה רגילה, בלי דפדפן, ומקבלת התראות.
          </p>
        </div>
        <button
          type="button"
          aria-label="לא עכשיו"
          onClick={dismiss}
          className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-sm bg-transparent text-muted"
        >
          <X size={18} strokeWidth={1.75} aria-hidden="true" />
        </button>
      </div>

      {/* אנדרואיד ומחשב: כפתור שבאמת מתקין */}
      {canPrompt && (
        <Button variant="primary" block onClick={() => void promptInstall()}>
          <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
          התקנה
        </Button>
      )}

      {/* אייפון: אין התקנה בלחיצה, יש שלושה צעדים */}
      {!canPrompt && platform === 'ios-safari' && (
        <>
          <Button variant="primary" block onClick={() => setShowSteps((open) => !open)}>
            <Share size={20} strokeWidth={1.75} aria-hidden="true" />
            {showSteps ? 'סגירת ההסבר' : 'איך מוסיפים באייפון'}
          </Button>

          {showSteps && (
            <ol className="m-0 grid list-none gap-3 rounded-md border border-line p-3">
              <Step index={1} icon={<Share size={20} strokeWidth={1.75} aria-hidden="true" />}>
                בתחתית המסך, לחצו על כפתור השיתוף.
              </Step>
              <Step index={2} icon={<Plus size={20} strokeWidth={1.75} aria-hidden="true" />}>
                גללו ברשימה ובחרו "הוספה למסך הבית".
              </Step>
              <Step
                index={3}
                icon={<Smartphone size={20} strokeWidth={1.75} aria-hidden="true" />}
              >
                לחצו "הוספה". האייקון יופיע במסך הבית.
              </Step>
            </ol>
          )}
        </>
      )}

      {!canPrompt && platform === 'ios-other' && (
        <p className="m-0 text-body-sm text-muted">
          באייפון אפשר להוסיף למסך הבית רק מ Safari. כדאי לפתוח את הכתובת שם.
        </p>
      )}

      {!canPrompt && (platform === 'android' || platform === 'desktop') && (
        <Link
          to="/install"
          className="min-h-11 content-center rounded-sm border border-muted text-center text-body font-medium text-ink no-underline"
        >
          איך מוסיפים למסך הבית
        </Link>
      )}
    </section>
  );
}

function Step({
  index,
  icon,
  children,
}: {
  index: number;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-center gap-3">
      <span className="grid size-10 shrink-0 place-items-center rounded-pill bg-olive-soft text-ink">
        {icon}
      </span>
      <span className="text-body-sm text-ink">
        <span className="font-medium">{index}. </span>
        {children}
      </span>
    </li>
  );
}
